import React, {useEffect, useState} from "react";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  split,
  fromPromise,
} from "@apollo/client";
import {GraphQLWsLink} from "@apollo/client/link/subscriptions";
import {createClient} from "graphql-ws";
import {setContext} from "@apollo/client/link/context";
import {onError} from "@apollo/client/link/error";
import {ApolloLink} from "@apollo/client";
import {RetryLink} from "@apollo/client/link/retry";
import PropTypes from "prop-types";
import {useAuth} from "../components/authentication/Context/AuthProvider";
import {getMainDefinition} from "@apollo/client/utilities";
import typePolicies from "../graphql/typePolicies";
import {some} from "underscore";

const isLocalDev = process.env.ENVIRONMENT_NAME === "localdev";
const graphqlURL = process.env.GRAPHQL_API;

const GraphQLClientProvider = ({children}) => {
  const {authSession, refreshSession, logout, setErrorMessage, orgName} = useAuth();
  const [apolloClient, setApolloClient] = useState();

  useEffect(() => {
    if (!authSession?.AccessToken) return;

    const httpLink = createHttpLink({
      uri: `${graphqlURL}/graphql`,
    });
    const authLink = setContext(async (_, {headers}) => {
      return {
        headers: {
          ...headers,
          orgName: orgName,
          Authorization: `Bearer ${authSession.IdToken}`,
          "X-Client-Platform": "web",
        },
      };
    });

    const wsLink = new GraphQLWsLink(
      createClient({
        url: `${graphqlURL.replace(/http/, "ws")}/graphql?orgName=${orgName}`,
        connectionParams: {
          authToken: `Bearer ${authSession.IdToken}`,
        },
      }),
    );

    const onErrorLink = onError(({graphQLErrors, response, operation, forward, networkError}) => {
      if (
        some(graphQLErrors, (error) => error.extensions?.exception?.code === "PlatformAccessDenied")
      ) {
        console.error("Access denied: PlatformAccessDenied");
        setErrorMessage("Access denied");
        logout();
        return;
      }
      if (
        some(
          graphQLErrors,
          (error) => error.extensions?.exception?.code === "MissingPlatformHeader",
        )
      ) {
        console.error("Access denied: Invalid");
        setErrorMessage("Access denied");
        logout();
        return;
      }
      if (networkError?.statusCode === 401) {
        return fromPromise(
          refreshSession().then((result) => {
            const token = result.IdToken;
            const oldHeaders = operation.getContext().headers;

            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: `Bearer ${token}`,
              },
            });

            return forward(operation);
          }),
        );
      }
    });

    const retryLink = new RetryLink({
      attempts: (count, operation, error) => {
        const definition = getMainDefinition(operation.query);
        if (definition.operation !== "query") {
          return false;
        }
        return count < 3;
      },
      delay: {
        jitter: true,
        initial: 1500,
      },
    });

    const splitLink = split(
      ({query}) => {
        const definition = getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      },
      wsLink,
      httpLink,
    );

    const links = ApolloLink.from([onErrorLink, authLink, retryLink, splitLink]);

    const client = new ApolloClient({
      link: links,
      cache: new InMemoryCache({
        typePolicies,
      }),
      devtools: {
        enabled: isLocalDev,
      },
    });

    setApolloClient(client);
  }, [authSession]);

  if (apolloClient) {
    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
  }

  return null;
};

GraphQLClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {GraphQLClientProvider};
